import React, {FC, useRef, useState} from 'react'
import {IProductProps, productActionType} from '../../cors/reducers'
import {useIntl} from 'react-intl'
import {useParams} from 'react-router-dom'
import {checkDuplicateBarcode} from "../../cors/_requests";
import SharedData from "../../../common/SharedData";
import {IBarcodeLookupResult, ICatalogPreview, ICatalogVariantPreview} from "../../../offers/cors/_models";
import {lookupBarcode} from "../../../offers/cors/_requests";
import BarcodeOfferCard from "../../../offers/match-by-barcode/BarcodeOfferCard";

type CustomProps = {
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  showLabel?: boolean
  flexClass?: boolean
}
type IProps = IProductProps & CustomProps

/**
 * Multi-supplier-aware barcode field.
 *
 * BEFORE: a barcode collision returned 400 "barcode already used by
 * another supplier" and disabled the Continue button — dead-end for
 * the supplier (they had to call support).
 *
 * AFTER: the field calls /offers/lookup-barcode which returns one of
 * three states. We render accordingly:
 *
 *   - available     → no extra UI; the existing wizard proceeds
 *   - match / already_yours / multiple_match → the barcode is already in
 *                     the catalog, so we render the offer form INLINE
 *                     (BarcodeOfferCard) right here — create, update, or
 *                     pick-a-variant — instead of navigating away. Continue
 *                     is blocked so the supplier can't also add it as a new
 *                     product.
 *
 * The legacy `checkDuplicateBarcode` POST is kept as a fallback — if
 * the new endpoint fails (network, older backend), we degrade to the
 * previous "show an error" behaviour rather than letting the supplier
 * submit a duplicate.
 *
 * In EDIT mode (`productID` set), this whole field is read-only and
 * the lookup is skipped — that flow is for revising an existing draft,
 * not for matching.
 */
const BarcodeField: FC<IProps> = ({prodState, prodDispatch, handleChange, showLabel=true}) => {
  const {formatMessage} = useIntl()
  const {productID} = useParams()
  const [isReadOnly, setIsReadOnly] = useState(typeof productID === 'string' || false)
  const {currentUser, language} = SharedData()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  // Multi-supplier match state. Any of these set → Continue is blocked
  // because the supplier should use the dedicated flow instead.
  const [match, setMatch] = useState<ICatalogPreview | null>(null)
  const [alreadyYours, setAlreadyYours] = useState<{
    product: ICatalogPreview
    product_supplier_id: number
  } | null>(null)
  // Multi-variant: barcode mapped to >1 catalog row (size/color variants).
  // Supplier picks which variant from the chooser.
  const [multipleMatch, setMultipleMatch] = useState<{
    barcode: string
    products: ICatalogVariantPreview[]
  } | null>(null)

  const clearAllStates = () => {
    setErrorMessage(null)
    setMatch(null)
    setAlreadyYours(null)
    setMultipleMatch(null)
    prodDispatch({type: productActionType.clearErrors, payload: {id: 'barcode'}})
  }

  // Debounce the lookup so we don't fire a request on every keystroke.
  // useRef is REQUIRED — a `let` inside the component body would reset
  // on every render (the parent's reducer dispatches re-render us on
  // every typed character), which made `clearTimeout` a no-op and
  // produced a request per keystroke. useRef survives renders.
  const lookupTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const generateBarcode = () => {
    const product_type = '1'
    const currDate = (Math.floor(Date.now() / 100) % Math.pow(10, 10)).toString()
    var checksum = calculateChecksum(product_type + currDate)
    var barcode = product_type + currDate + checksum
    var payload = {id: 'barcode', value: barcode}
    prodDispatch({type: productActionType.text, payload})
    prodDispatch({type: productActionType.barcode_generated, payload})

    setIsReadOnly(true)
    clearAllStates()
  }

  const handleChangeBarcode = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(e)
    clearAllStates()

    // Don't fire lookup until barcode is at least minimally valid
    // (length > 3, non-empty). Saves needless API calls during typing.
    const v = e.target.value.trim()
    if (v === '' || v.length < 4) return

    if (lookupTimer.current) clearTimeout(lookupTimer.current)
    lookupTimer.current = setTimeout(() => {
      void runLookup(v)
    }, 400)
  }

  const runLookup = async (barcode: string) => {
    try {
      const res = await lookupBarcode(barcode, currentUser?.token, language)
      handleLookupResult(res.data)
    } catch {
      // New endpoint failed — degrade to the legacy duplicate check so
      // we never let the supplier submit a colliding barcode silently.
      await legacyDuplicateCheck(barcode)
    }
  }

  const handleLookupResult = (data: IBarcodeLookupResult) => {
    if (data.state === 'available') {
      clearAllStates()
      return
    }
    if (data.state === 'pending_request') {
      // Already in the new-product request queue (pending review). Block
      // Continue so the supplier doesn't file the same product twice.
      clearAllStates()
      prodDispatch({
        type: productActionType.setError,
        payload: {
          id: 'barcode',
          errorMessage: formatMessage({id: 'offers.pending_request.continue_blocked'}),
        },
      })
      return
    }
    if (data.state === 'match') {
      setMatch(data.product)
      // Set the reducer error to block Continue — supplier should use
      // the inline CTA, not the legacy wizard's submit.
      prodDispatch({
        type: productActionType.setError,
        payload: {id: 'barcode', errorMessage: formatMessage({id: 'offers.match.continue_blocked'})},
      })
      return
    }
    if (data.state === 'already_yours') {
      setAlreadyYours({product: data.product, product_supplier_id: data.product_supplier_id})
      prodDispatch({
        type: productActionType.setError,
        payload: {
          id: 'barcode',
          errorMessage: formatMessage({id: 'offers.already_yours.continue_blocked'}),
        },
      })
      return
    }
    if (data.state === 'multiple_match') {
      // Same blocked-Continue treatment as `match` — the supplier needs
      // to use the inline variant picker, not the legacy wizard's submit.
      // When the supplier already owns offers on EVERY variant, swap the
      // error string for a more accurate one: 'Add my offer' is no longer
      // the right CTA (the list will only show 'View your offer' buttons).
      const allOwned = data.products.every((p) => p.has_my_offer)
      setMultipleMatch({
        barcode: data.products[0]?.barcode ?? '',
        products: data.products,
      })
      prodDispatch({
        type: productActionType.setError,
        payload: {
          id: 'barcode',
          errorMessage: formatMessage({
            id: allOwned
              ? 'offers.already_yours.continue_blocked'
              : 'offers.match.continue_blocked',
          }),
        },
      })
    }
  }

  const legacyDuplicateCheck = async (barcode: string) => {
    try {
      const result = await checkDuplicateBarcode(barcode, currentUser?.token, language)
      if (result.data === '1') {
        const msg = formatMessage({id: 'barcode_exists_message'})
        prodDispatch({
          type: productActionType.setError,
          payload: {id: 'barcode', errorMessage: msg},
        })
      } else if (barcode.length > 25) {
        prodDispatch({
          type: productActionType.setError,
          payload: {id: 'barcode', errorMessage: formatMessage({id: 'barcode_length_error'})},
        })
      } else {
        clearAllStates()
      }
    } catch (error) {
      const apiMessage =
        typeof error === 'object' && error !== null && 'response' in error
          ? (error as any).response?.data?.message
          : null
      prodDispatch({
        type: productActionType.setError,
        payload: {
          id: 'barcode',
          errorMessage: apiMessage || formatMessage({id: 'barcode_exists_message'}),
        },
      })
    }
  }

  function calculateChecksum(digits: string, barcode_type: string = 'UPCA'): number {
    const odd_sum: number =
      parseInt(digits[0]) +
      parseInt(digits[2]) +
      parseInt(digits[4]) +
      parseInt(digits[6]) +
      parseInt(digits[8]) +
      parseInt(digits[10])
    const even_sum: number =
      parseInt(digits[1]) +
      parseInt(digits[3]) +
      parseInt(digits[5]) +
      parseInt(digits[7]) +
      parseInt(digits[9])

    let total_sum: number

    if (barcode_type === 'UPCA') {
      total_sum = odd_sum * 3 + even_sum
    } else {
      total_sum = odd_sum + even_sum * 3
      total_sum += parseInt(digits[11]) * 3
    }

    const mod_10: number = total_sum % 10

    if (mod_10 === 0) {
      return 0
    }

    return 10 - mod_10
  }

  // The barcode that resolved to an existing catalog row (any of the three
  // in-catalog states). Drives the inline offer card below.
  const inCatalogBarcode =
    match?.barcode ??
    alreadyYours?.product.barcode ??
    multipleMatch?.barcode ??
    null

  // Explicit "Look up" — fires the same lookup the debounced typing handler
  // does, but immediately (skips the 400ms wait).
  const handleLookupClick = () => {
    const v = (prodState.barcode.value ?? '').toString().trim()
    if (v.length < 4) return
    if (lookupTimer.current) clearTimeout(lookupTimer.current)
    void runLookup(v)
  }

  return (
      <div className='aps-barcode-box'>
        <div className='aps-barcode-card'>
          {showLabel && (
            <label className='aps-barcode-label' htmlFor='barcode'>
              {formatMessage({ id: 'barcode' })} <span className='aps-req'>*</span>
            </label>
          )}
          <div className='aps-barcode-row'>
            <div className='aps-barcode-input-wrap'>
              <i className='fas fa-barcode aps-barcode-icon' aria-hidden='true' />
              <input
                id='barcode'
                type='text'
                className={`aps-barcode-input${prodState.barcode.error ? ' is-invalid' : ''}`}
                value={prodState.barcode.value || ''}
                onChange={handleChangeBarcode}
                readOnly={isReadOnly === true}
                placeholder={formatMessage({ id: 'barcode' })}
                autoComplete='off'
              />
            </div>
            {typeof productID !== 'string' && (
              <div className='aps-barcode-actions'>
                <button type='button' className='aps-btn-lookup' onClick={handleLookupClick} disabled={isReadOnly}>
                  {formatMessage({ id: 'look_up' })}
                </button>
                <button type='button' className='aps-btn-generate' onClick={generateBarcode} disabled={isReadOnly}>
                  {formatMessage({ id: 'generate' })}
                </button>
              </div>
            )}
          </div>

          {prodState.barcode.error && prodState.barcode.msg ? (
            <div className='aps-barcode-error'>{prodState.barcode.msg}</div>
          ) : null}
          {errorMessage ? (
            <div id='barcode-field-error' className='aps-barcode-error'>{errorMessage}</div>
          ) : null}
          {showLabel && typeof productID !== 'string' ? (
            <div className='aps-no-barcode'>{formatMessage({ id: 'no_barcode_msg' })}</div>
          ) : null}
        </div>

        {/* Multi-supplier match-by-barcode UI. When the barcode is already
            in the catalog (match / already_yours / one-of-many variants),
            render the offer form INLINE here instead of navigating to a
            separate page — BarcodeOfferCard handles create / update /
            variant-pick on its own. Continue stays blocked (set above) so
            the supplier can't also submit this as a new product.
            prefillOfferValues defaults to true so when the supplier already
            owns the offer ("you already sell this product"), the form starts
            from their current price + quantity instead of blank. */}
        {inCatalogBarcode ? <BarcodeOfferCard barcode={inCatalogBarcode} /> : null}
      </div>
  );
}

export default BarcodeField
